import type { FilterSchema } from '@/components/ResourcesSidebar/ResourcesSidebarHelpers';

/**
 * Single place where every GA (gtag) event of the portal is defined, so the
 * event names and their parameters stay consistent across pages. Nothing
 * outside this module should touch `window.gtag` directly.
 *
 * Everything is reached through the `ga` namespace, so a call site always
 * reads as analytics:
 *
 *   import { ga } from '@/lib/analytics';
 *   ga.trackFilterApply({ filter_type, filter_value, page });
 *
 * Naming inside the namespace:
 *   `track*`  sends an event
 *   `set*`    sets state applied to every subsequent event
 *   others    pure helpers that build parameter values
 */

export type GAEventParams = Record<string, unknown>;

const PAGES = {
  home: 'home',
  games: 'games',
  compliance: 'compliance',
  marketing: 'marketing',
  gameDetail: 'game_detail',
  globalSearch: 'global_search',
} as const;

export type GAPage = (typeof PAGES)[keyof typeof PAGES] | string;

/**
 * Sidebar filter keys mapped onto the filter_type values agreed with the
 * analytics team. `page` disambiguates the file type/format filters, which
 * exist on both the compliance and the marketing page.
 */
const FILTER_TYPES: Record<string, string> = {
  market: 'market',
  language: 'language',
  gameStatus: 'game_status',
  volatility: 'volatility',
  gameFeatures: 'game_feature',
  gameGroups: 'game_group',
  gameProviders: 'game_provider',
  gameFamily: 'game_family',
  releaseDate: 'release_date',
  rtp: 'rtp',
  fileTypeCompliance: 'file_type',
  fileFormatCompliance: 'file_format',
  fileTypeMarketing: 'file_type',
  fileFormatMarketing: 'file_format',
  partnerType: 'partner_type',
  platform: 'platform',
  operator: 'operator',
  brand: 'brand',
};

// --- state -----------------------------------------------------------

/**
 * Sets the GA4 user_id to the Auth0 `sub` claim so authenticated sessions
 * can be attributed to a specific account. `sub` is an opaque identifier
 * (e.g. "auth0|abc123") and never contains an email address or other PII.
 */
const setUserId = (userId: string | undefined) => {
  window?.gtag?.('set', 'user_id', userId);
};

const clearUserId = () => {
  setUserId(undefined);
};

/** Sets parameters applied to every subsequent event. */
const setParams = (params: GAEventParams) => {
  window?.gtag?.('set', params);
};

// --- events ----------------------------------------------------------

const trackEvent = (event: string, params: GAEventParams = {}) => {
  window?.gtag?.('event', event, params);
};

const trackPageView = (params: {
  page_title: string;
  page_location: string;
  page_path: string;
}) => trackEvent('page_view', params);

const trackMouseClick = (
  params: GAEventParams & { component: string },
) => trackEvent('mouse_click', params);

const trackMouseOver = (
  params: GAEventParams & { component: string; section: string },
) => trackEvent('mouse_over', params);

const trackTabView = (
  params: GAEventParams & { tab_name: string; page?: GAPage },
) => trackEvent('tab_view', params);

const trackSectionInteraction = (params: {
  section: string;
  dwell_time_ms: number;
}) => trackEvent('section_interaction', params);

/** `page` is the zero-based page index that was just requested. */
const trackLoadMore = (params: { section: string; page: number }) =>
  trackEvent('load_more', params);

const trackAssetDownload = (params: {
  asset_id: number;
  asset_name: string;
  asset_format?: string;
}) => trackEvent('asset_download', params);

const trackAssetMultiSelect = (params: { assets_count: number }) =>
  trackEvent('asset_multi_select', params);

const trackAssetBulkDownload = (params: { assets_count: number }) =>
  trackEvent('asset_bulk_download', params);

/**
 * `page` is set globally by GALocationTracker on each route change, so it
 * only needs passing when the event happens outside a route (the global
 * search modal) or should report a different page than the current one.
 */
const trackFilterApply = (params: {
  filter_type: string;
  filter_value: string;
  page?: GAPage;
}) => trackEvent('filter_apply', params);

const trackSortApply = (params: {
  sort_by: string;
  sort_order?: 'asc' | 'desc';
  page?: GAPage;
}) => trackEvent('sort_apply', params);

const trackSearchAccept = (params: {
  search_term: string;
  result_count?: number;
  search_scope?: string;
  page?: GAPage;
}) => trackEvent('search_accept', params);

const trackSearchResultClick = (params: {
  result_type: string;
  result_id: string | number;
  position: number;
  search_term: string;
  page?: GAPage;
}) =>
  trackEvent('search_result_click', {
    page: PAGES.globalSearch,
    ...params,
  });

const trackGamesBulkDownload = (params: {
  total_games: number;
  filters_applied: string;
  search_term: string;
  page?: GAPage;
}) =>
  trackEvent('games_bulk_download', {
    page: PAGES.games,
    ...params,
  });

// --- helpers ---------------------------------------------------------

const pageName = (pathname: string): GAPage => {
  if (pathname === '/') return PAGES.home;
  if (pathname.startsWith('/games')) return PAGES.games;
  if (pathname.startsWith('/compliance')) return PAGES.compliance;
  if (pathname.startsWith('/marketing')) return PAGES.marketing;
  if (pathname.startsWith('/game/')) return PAGES.gameDetail;

  return pathname.replace(/^\//, '').replace(/\//g, '_') || PAGES.home;
};

const filterType = (filterKey: string) =>
  FILTER_TYPES[filterKey] || filterKey;

/**
 * GA only accepts scalar parameters, so filter values are flattened:
 * multi-select values are comma separated, ranges (rtp, volatility) are
 * joined with a dash and a release date range with two dots.
 */
const formatFilterValue = (
  type: string,
  value: unknown,
): string => {
  if (value == null) return '';

  if (Array.isArray(value)) {
    const items = value.filter(
      (item) => item != null && item !== '',
    );
    if (items.length === 0) return '';

    if (type === 'release_date') {
      return items.map(String).join('..');
    }

    return items
      .map((item) =>
        Array.isArray(item)
          ? item.map(String).join('-')
          : String(item),
      )
      .join(',');
  }

  return String(value);
};

/** Comma separated list of the filter types currently applied. */
const formatFiltersApplied = (
  filters?: Partial<FilterSchema>,
): string =>
  Object.entries(filters || {})
    .filter(([key, value]) => {
      if (key === 'navigation') return false;
      return Array.isArray(value) ? value.length > 0 : Boolean(value);
    })
    .map(([key]) => filterType(key))
    .join(',');

export const ga = {
  PAGES,
  FILTER_TYPES,

  setUserId,
  clearUserId,
  setParams,

  trackEvent,
  trackPageView,
  trackMouseClick,
  trackMouseOver,
  trackTabView,
  trackSectionInteraction,
  trackLoadMore,
  trackAssetDownload,
  trackAssetMultiSelect,
  trackAssetBulkDownload,
  trackFilterApply,
  trackSortApply,
  trackSearchAccept,
  trackSearchResultClick,
  trackGamesBulkDownload,

  pageName,
  filterType,
  formatFilterValue,
  formatFiltersApplied,
} as const;
